import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup, Row, Col } from "reactstrap";
import { vendorInvoiceAPI } from "../../../../../helpers/api/vendorInvoice";

const ADD_CATEGORY_VALUE = "__add__";

const EMPTY = {
  document: "", name: "", cif: "", issue_date: "", due_date: "",
  amount_without_tax: 0, tax_amount: 0, total_amount: 0, currency: "RON", observation: "",
  phase: { phase_id: null, phase_name: null },
  category: { category_id: null, category_name: null },
  sub_category: { sub_category_id: null, sub_category_name: null },
  block: { block_id: null, block_name: null },
};

const EditInvoiceModal = ({ open, invoice, index, categories, subCategories, phases, onClose, onSave, reloadCategories }) => {
  const [form, setForm] = useState(EMPTY);
  const [applyToVendor, setApplyToVendor] = useState(false);

  // Inline "add category" sub-form
  const [adding,   setAdding]   = useState(false);
  const [newCat,   setNewCat]   = useState("");
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);

  useEffect(() => {
    if (open && invoice) {
      setForm({
        document: invoice.document || "",
        name: invoice.name || "",
        cif: invoice.cif || "",
        issue_date: invoice.issue_date || "",
        due_date: invoice.due_date || "",
        amount_without_tax: invoice.amount_without_tax ?? 0,
        tax_amount: invoice.tax_amount ?? 0,
        total_amount: invoice.total_amount ?? 0,
        currency: invoice.currency || "RON",
        observation: invoice.observation || "",
        phase: invoice.phase || EMPTY.phase,
        category: invoice.category || EMPTY.category,
        sub_category: invoice.sub_category || EMPTY.sub_category,
        block: invoice.block || EMPTY.block,
      });
      setApplyToVendor(false);
      setAdding(false); setNewCat(""); setCreateErr(null);
    }
  }, [open, invoice]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const phaseBlocks = phases?.find((p) => p.id === form.phase?.phase_id)?.blocks || [];

  const handlePhase = (val) => {
    const id = val ? parseInt(val, 10) : null;
    const p = phases?.find((x) => x.id === id);
    // Block belongs to a phase, so reset it when the phase changes
    setForm((f) => ({ ...f, phase: { phase_id: id, phase_name: p?.name || null }, block: { block_id: null, block_name: null } }));
  };
  const handleCategory = (val) => {
    if (val === ADD_CATEGORY_VALUE) { setAdding(true); setCreateErr(null); return; }
    const id = val ? parseInt(val, 10) : null;
    const c = categories.find((x) => x.id === id);
    set("category", { category_id: id, category_name: c?.name || null });
  };
  const handleSub = (val) => {
    const id = val ? parseInt(val, 10) : null;
    const s = subCategories.find((x) => x.id === id);
    set("sub_category", { sub_category_id: id, sub_category_name: s?.name || null });
  };
  const handleBlock = (val) => {
    const id = val ? parseInt(val, 10) : null;
    const b = phaseBlocks.find((x) => x.id === id);
    set("block", { block_id: id, block_name: b?.name || null });
  };

  const handleCreateCategory = async () => {
    if (!newCat.trim()) { setCreateErr("Please enter a category name"); return; }
    setCreating(true); setCreateErr(null);
    try {
      const res = await vendorInvoiceAPI.createCategory({ name: newCat.trim() });
      const created = res?.data || res;
      await reloadCategories?.();
      const id = created?.id;
      if (id) set("category", { category_id: parseInt(id, 10), category_name: created?.name || newCat.trim() });
      setAdding(false); setNewCat("");
    } catch (err) {
      setCreateErr(err?.response?.data?.name?.[0] || "Failed to create category");
    } finally {
      setCreating(false);
    }
  };

  const handleSave = () => onSave({ index, data: { ...form }, applyToVendor });

  if (!invoice) return null;

  return (
    <Modal isOpen={open} toggle={onClose} size="lg" centered scrollable>
      <ModalHeader toggle={onClose}>Edit Invoice</ModalHeader>
      <ModalBody>
        <Row className="g-3">
          <Col md={4}>
            <Label className="form-label small fw-semibold mb-1">Document</Label>
            <Input bsSize="sm" value={form.document} onChange={(e) => set("document", e.target.value)} className={!form.document ? "border-danger" : ""} />
          </Col>
          <Col md={5}>
            <Label className="form-label small fw-semibold mb-1">Vendor</Label>
            <Input bsSize="sm" value={form.name} onChange={(e) => set("name", e.target.value)} className={!form.name ? "border-danger" : ""} />
          </Col>
          <Col md={3}>
            <Label className="form-label small fw-semibold mb-1">CIF</Label>
            <Input bsSize="sm" value={form.cif} onChange={(e) => set("cif", e.target.value)} className={!form.cif ? "border-danger" : ""} />
          </Col>

          <Col md={3}>
            <Label className="form-label small fw-semibold mb-1">Issue Date</Label>
            <Input type="date" bsSize="sm" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} className={!form.issue_date ? "border-danger" : ""} />
          </Col>
          <Col md={3}>
            <Label className="form-label small fw-semibold mb-1">Due Date</Label>
            <Input type="date" bsSize="sm" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} />
          </Col>
          <Col md={3}>
            <Label className="form-label small fw-semibold mb-1">Currency</Label>
            <Input type="select" bsSize="sm" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
              <option value="RON">RON</option>
              <option value="EUR">EUR</option>
            </Input>
          </Col>

          <Col md={4}>
            <Label className="form-label small fw-semibold mb-1">Amount (no tax)</Label>
            <Input type="number" step="0.01" bsSize="sm" value={form.amount_without_tax} onChange={(e) => set("amount_without_tax", parseFloat(e.target.value) || 0)} />
          </Col>
          <Col md={4}>
            <Label className="form-label small fw-semibold mb-1">Tax</Label>
            <Input type="number" step="0.01" bsSize="sm" value={form.tax_amount} onChange={(e) => set("tax_amount", parseFloat(e.target.value) || 0)} />
          </Col>
          <Col md={4}>
            <Label className="form-label small fw-semibold mb-1">Total</Label>
            <Input type="number" step="0.01" bsSize="sm" value={form.total_amount} onChange={(e) => set("total_amount", parseFloat(e.target.value) || 0)} />
          </Col>

          <Col md={3}>
            <Label className="form-label small fw-semibold mb-1">Phase</Label>
            <Input type="select" bsSize="sm" value={form.phase?.phase_id || ""} onChange={(e) => handlePhase(e.target.value)} className={!form.phase?.phase_id ? "border-danger" : ""}>
              <option value="">Select phase</option>
              {phases?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Input>
          </Col>
          <Col md={3}>
            <Label className="form-label small fw-semibold mb-1">Category</Label>
            {adding ? (
              <div className="d-flex gap-1">
                <Input bsSize="sm" autoFocus value={newCat} placeholder="New category" onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleCreateCategory()} />
                <Button size="sm" color="success" disabled={creating} onClick={handleCreateCategory} title="Create"><i className="ri-check-line" /></Button>
                <Button size="sm" color="light" onClick={() => { setAdding(false); setCreateErr(null); }} title="Cancel"><i className="ri-close-line" /></Button>
              </div>
            ) : (
              <Input type="select" bsSize="sm" value={form.category?.category_id || ""} onChange={(e) => handleCategory(e.target.value)} className={!form.category?.category_id ? "border-danger" : ""}>
                <option value="">Select category</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                <option value={ADD_CATEGORY_VALUE}>+ Add category</option>
              </Input>
            )}
            {createErr && <small className="text-danger d-block mt-1">{createErr}</small>}
          </Col>
          <Col md={3}>
            <Label className="form-label small fw-semibold mb-1">Sub-category</Label>
            <Input type="select" bsSize="sm" value={form.sub_category?.sub_category_id || ""} onChange={(e) => handleSub(e.target.value)}>
              <option value="">— None —</option>
              {subCategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Input>
          </Col>
          <Col md={3}>
            <Label className="form-label small fw-semibold mb-1">Block</Label>
            <Input type="select" bsSize="sm" value={form.block?.block_id || ""} onChange={(e) => handleBlock(e.target.value)} disabled={!form.phase?.phase_id || phaseBlocks.length === 0}>
              <option value="">{form.phase?.phase_id ? "— None —" : "Select phase first"}</option>
              {phaseBlocks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Input>
          </Col>

          <Col md={12}>
            <Label className="form-label small fw-semibold mb-1">Observation</Label>
            <Input type="textarea" rows={2} bsSize="sm" value={form.observation} onChange={(e) => set("observation", e.target.value)} />
          </Col>

          <Col md={12}>
            <FormGroup check className="mt-1">
              <Input type="checkbox" id="applyVendor" checked={applyToVendor} onChange={(e) => setApplyToVendor(e.target.checked)} />
              <Label check for="applyVendor" className="small text-muted">
                Also apply Phase, Category, Sub-category &amp; Block to every invoice from this vendor (CIF {form.cif || "—"})
              </Label>
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose}>Cancel</Button>
        <Button color="primary" onClick={handleSave}><i className="ri-save-line me-1" />Save Changes</Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditInvoiceModal;
